import { Button, Group, Paper, Stack, Text, TextInput } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { getGetMeQueryKey, useUpdateMe } from "../../api/generated/endpoints/identity/identity";
import type { Me } from "../../api/generated/model";
import { MeUpdate, meUpdateNameMax } from "../../api/generated/zod";
import { ErrorAlert } from "../../platform/ErrorAlert";
import classes from "./account.module.css";

/** What is wrong with a trimmed name, by the contract's own schema; `undefined` when it may be saved. */
function nameProblem(name: string): string | undefined {
  if (MeUpdate.safeParse({ name }).success) return undefined;
  return name.length > meUpdateNameMax ? `Use ${meUpdateNameMax} characters or fewer.` : "Name is required";
}

/**
 * Live's Profile card: the email, which cannot change, and the name, saved with `PATCH /v1/me`.
 * "Save changes" waits for a changed, valid name, as live's does. The answer goes into the
 * `GET /v1/me` cache, so the header shows the new name at once, and live's "Name updated" shows
 * beside the button: we have no toasts (spec web/account-settings, decision 2).
 */
export function ProfileSection({ me }: { me: Me }) {
  const saved = me.name ?? "";
  const [name, setName] = useState(saved);
  const [touched, setTouched] = useState(false);
  const [updated, setUpdated] = useState(false);
  const queryClient = useQueryClient();
  // The header's name is updated by the mutation's own callback, which runs even after this page is
  // left mid-save; a callback given to `mutate` would not (spec web/account-settings, decision 7).
  const update = useUpdateMe({
    mutation: { onSuccess: (answer) => queryClient.setQueryData(getGetMeQueryKey(), answer) },
  });
  const trimmed = name.trim();
  const problem = nameProblem(trimmed);
  const savable = trimmed !== saved && problem === undefined && !update.isPending;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTouched(true);
    if (!savable) return;
    update.mutate(
      { data: { name: trimmed } },
      {
        onSuccess: (answer) => {
          setName(answer.name ?? trimmed);
          setUpdated(true);
        },
      },
    );
  };

  return (
    <Paper className={classes.card}>
      <Stack gap={4}>
        <Text className={classes.cardTitle}>Profile</Text>
        <Text className={classes.cardDescription}>How your name appears to your teammates.</Text>
      </Stack>
      <TextInput
        label="Email"
        value={me.email}
        disabled
        description="Your email address cannot be changed."
      />
      <form onSubmit={submit} noValidate>
        <Stack gap="sm">
          <TextInput
            label="Name"
            placeholder="Enter your name"
            withAsterisk
            value={name}
            // A pending save keeps the field: an edit now would reset the mutation and lose its answer.
            readOnly={update.isPending}
            onChange={(event) => {
              if (update.isPending) return;
              setName(event.currentTarget.value);
              setUpdated(false);
              update.reset();
            }}
            onBlur={() => setTouched(true)}
            error={touched ? problem : undefined}
          />
          {update.isError ? <ErrorAlert error={update.error} /> : null}
          <Group justify="flex-end" gap="md">
            {updated && trimmed === saved ? (
              <Text component="output" size="sm" c="var(--cai-color-text)">
                Name updated
              </Text>
            ) : null}
            <Button type="submit" radius="md" loading={update.isPending} disabled={!savable}>
              Save changes
            </Button>
          </Group>
        </Stack>
      </form>
    </Paper>
  );
}
